import { Business, Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, CircularProgress, IconButton, Modal, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../Redux/store';
import { BusinessInformation } from './BusinessInformation';
import { PhotoUpload } from '../PhotoUpload';
import { resetState, setClientProfilePicture, setProfileCreationUserId, setStep } from '../../Slices/ClientProfileCreationSlice';
import { useUpdateClientMutation, useUploadClientProfilePictureMutation } from '../../../../../Redux/api/clientApi';
import { CreatingProfile } from '../CreatingProfile';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../../Hooks/useToast';

interface ClientCreationModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
}
export const ClientCreationModal = ({open, setOpen}: ClientCreationModalProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const profileCreationSlice = useAppSelector( state => state.ClientProfileCreation);
    const userId = useAppSelector( state => state.Global.userId);    
    const [uploadPhoto, {data: uploadResponse, isLoading: isUploading}] = useUploadClientProfilePictureMutation();
    const [updateClient, {data: clientUpdateResponse, isLoading: clientUpdateResponseLoading}] = useUpdateClientMutation();
    /* ******************** Variables ******************** */
    /* ******************** Functions ******************** */
    const setUploadedFile= (file: File | undefined) => {
        dispatch(setClientProfilePicture(file));
    }
    const formatLanguages = () =>{
        let languages = [];
        if (profileCreationSlice.primaryLanguage) languages.push(profileCreationSlice.primaryLanguage);
        if (profileCreationSlice.secondaryLanguage) languages.push(profileCreationSlice.secondaryLanguage);
        return languages;
    }
    const handleClose = () => {
        setOpen(false);
    }
    const handleNext = () => {
        const nextStep = profileCreationSlice.step+1
        if (nextStep <= 2) {
            dispatch(setStep(nextStep))
        } 
        if (profileCreationSlice.step == 2)  {
            if (profileCreationSlice.profilePicture) {
                let photoFormData = new FormData();
                photoFormData.append('file', profileCreationSlice.profilePicture);
                uploadPhoto({locator: userId!, body: photoFormData})
                .unwrap()
                .then(res => console.log(res))
                .catch(err => console.log(err));
            }
            const updateClientRequestBody = {
                locator: userId,
                updated_data: {
                    description: profileCreationSlice.summary,
                    company: profileCreationSlice.company,
                    country: profileCreationSlice.country,
                    languages: formatLanguages()
                }
            }
            
            updateClient(updateClientRequestBody)
            .unwrap()
            .then(res =>
                navigate(`/profile/client`)
            )
            .catch(err => {
                console.log(err)
                showToast({
                    text: "Something went wrong",
                })
            });
        }
    }
    const handleBack = () => {
        const prevStep = profileCreationSlice.step-1
        if (prevStep >= 1) {
            dispatch(setStep(prevStep))
        } 
    }
    /* ******************** Effects ******************** */
    useEffect(() => {
        if (userId !== profileCreationSlice.userId){
            dispatch(resetState())
            dispatch(setProfileCreationUserId(userId));
        }
    }, [])
    /* ******************** JSX ******************** */
    return (
        <Modal
            open={open}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Card
                sx={{
                    width: "600px",
                    borderRadius: "12px",
                    maxHeight: "95%"
                }}
            >
                <CardHeader 
                    sx={{
                        paddingBottom: 1
                    }}                
                    action={
                        <IconButton onClick={handleClose}>
                            <Close color='primary'/>
                        </IconButton>
                    }
                />
                <CardContent
                    sx={{
                        padding: '0 24px'
                    }}
                >
                    {
                        isUploading || clientUpdateResponseLoading ?
                        <CreatingProfile />:
                        (profileCreationSlice.step == 1 ? 
                        <BusinessInformation /> : 
                        <PhotoUpload uploadedFile={profileCreationSlice.profilePicture} setUploadedFile={setUploadedFile}/>)
                    }
                </CardContent>
                <CardActions
                    sx={{
                        padding: '24px'
                    }}
                >
                    {
                        isUploading || clientUpdateResponseLoading ?
                        null
                        :
                        <Box display={'flex'} flexDirection={'row'} width={'100%'}>
                            <Box flex={1} display={'flex'} alignItems={'center'}>
                                <Typography variant={'body-small'} color={'black.300'}>Step {profileCreationSlice.step} of 2</Typography>
                            </Box>
                            <Box flex={1} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>                            
                                { profileCreationSlice.step > 1 && <Button variant={'outlined'} onClick={handleBack}>Back</Button>}
                                <Button 
                                    variant={'contained'} 
                                    onClick={handleNext} 
                                    disabled={profileCreationSlice.nextDisabled}
                                    sx={{
                                        "&.Mui-disabled": {
                                            backgroundColor: 'black.50',
                                            border: 'none',
                                            color: 'primary.main'
                                        }
                                    }}
                                >
                                    {profileCreationSlice.step == 2  ? 'Create profile' : 'Next'}
                                </Button>
                            </Box>
                        </Box>
                    }
                </CardActions>
            </Card> 
        </Modal>
    )
}